import React, { useState, useEffect, useContext } from 'react';
import { Modal } from 'antd';
import { AudioOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePreventRecordingAbandon } from '../../hooks/usePreventRecordingAbandon';
import completeSession from '../../utils/session/completeSession';
import { displayWarningModal } from '../../utils/errorHandling';
import TestErrorComponent from './TestErrorComponent';
import { toast } from "react-hot-toast";

import PatientForm from '../../components/patient/PatientForm';
import RecordingActions from './RecordingActions';
import { logEvent } from "../../analytics";
import VisitModalContent from './VisitModalContent';
import confirmVisitDiscard from './VisitDiscardConfirm';
import RecordingContext from './RecordingContext';

export default function RecordingModal({ 
  patient = null,
  fromPatientDetails = false,
  open = false,
  elapsedTime = 0,
  onShowNoteTypeSelector
}) {
  const { t } = useTranslation();
  const ctx = useContext(RecordingContext);
  const [isStarting, setIsStarting] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [recordingModalState, setRecordingModalState] = useState('initial');
  const [selectedPatient, setSelectedPatient] = useState(patient);
  const [editingPatient, setEditingPatient] = useState(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [showErrorButtons, setShowErrorButtons] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);

  // Use context for timer and recording state
  const {
    isRecording,
    isPaused,
    sessionUuid,
    totalChunks,
    problemStats,
    startRecording,
    pauseRecording,
    endRecording,
    setShowRecordingModal
  } = ctx;

  usePreventRecordingAbandon(isRecording, sessionUuid);

  // Check for query parameter on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error_buttons') === '1') {
      setShowErrorButtons(true);
    }
  }, []);

  useEffect(() => {
    if (isPaused) {
      setRecordingModalState('paused');
    } else if (isRecording) {
      setRecordingModalState('recording');
    } else {
      setRecordingModalState('initial');
    }
  }, [isPaused, isRecording]);

  const handleStart = async () => {
    if (isStarting) return;
    setIsStarting(true);
    setIsCollapsing(true);
    setTimeout(async () => {
      await startRecording();
      setRecordingModalState('recording');
      setIsStarting(false);
      setIsCollapsing(false);
      setShowRecordingModal(false); // Actually close modal after animation
    }, 400); // Match animation duration
  };

  const handlePause = async () => {
    await pauseRecording();
    setRecordingModalState('paused');
  };

  const handleResume = async () => {
    setIsCollapsing(true);
    setTimeout(async () => {
      await ctx.resumeRecording();
      setRecordingModalState('recording');
      setIsCollapsing(false);
      setShowRecordingModal(false); // Actually close modal after animation
    }, 400); // Match animation duration
  };

  const handleFinalize = async () => {
    setIsFinalizing(true);
    if (!sessionUuid || totalChunks < 1 || elapsedTime < 30) {
      displayWarningModal(
        t('session_recorder.errors.too_short_title'),
        t('session_recorder.errors.too_short_body'),
        'rec-too-short-warning-modal'
      );
      setIsFinalizing(false);
      return;
    }

    const success = await completeSession(sessionUuid, selectedPatient?.getUUID(), totalChunks, problemStats);
    // even if we couldn't complete the session, we still want to end the recording and close the modal, without showing error
    await endRecording();
    if (success) {
      if (onShowNoteTypeSelector) onShowNoteTypeSelector({ sessionUuid, patient: selectedPatient });
      setShowRecordingModal(false);
    } else {
      toast(t('session_recorder.errors.complete_warning'), {
        icon: '🛜' 
      });
      setShowRecordingModal(false);
    }
    logEvent('SessionRecorder', 'Complete');
    setIsFinalizing(false);
  };

  const handleClose = async () => {
    if (recordingModalState === 'initial' || !sessionUuid) {
      await endRecording();
      setShowRecordingModal(false);
      return;
    }
    const confirmed = await confirmVisitDiscard({ sessionUuid, t });
    if (confirmed) {
      await endRecording();
      setShowRecordingModal(false);
    }
  };

  const handlePatientChange = (newPatient) => {
    setSelectedPatient(newPatient);
    ctx.setPatient(newPatient);
  };

  return (
    <>
      <Modal
        key="recording-modal"
        forceRender
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ width: '24px', display: 'inline-block' }}>
              {recordingModalState === 'recording' && <AudioOutlined className='iconSpacing' />}
            </span>
            {t(`recording_modal.${recordingModalState}_title`)}
          </div>
        }
        open={open || isCollapsing}
        onCancel={handleClose}
        footer={null}
        width={800}
        destroyOnClose={false}
        closable={true}
        maskClosable={false}
        data-testid="recording-modal"
        modalRender={modal => (
          <div className={isCollapsing ? 'collapsing-inner' : ''}>{modal}</div>
        )}
      >
        <VisitModalContent
          initialPatient={patient}
          sessionUuid={sessionUuid}
          fromPatientDetails={fromPatientDetails}
          onPatientChange={handlePatientChange}
        >
          {/* Recording actions in a fixed-height container */}
          <div>
            <RecordingActions
              recordingModalState={recordingModalState}
              onStart={handleStart}
              onPause={handlePause}
              onResume={handleResume}
              onFinalize={handleFinalize}
              isFinalizing={isFinalizing}
              isStarting={isStarting}
            />
          </div>

          {showErrorButtons && (
            <TestErrorComponent />
          )}
        </VisitModalContent>
      </Modal>

      <PatientForm
        isOpen={isPatientModalOpen}
        editingPatient={editingPatient}
        onCancel={() => {
          setIsPatientModalOpen(false);
          setEditingPatient(null);
        }}
        onSave={(savedPatient) => {
          setSelectedPatient(savedPatient);
          ctx.setPatient(savedPatient);
          setIsPatientModalOpen(false);
          setEditingPatient(null);
        }}
      />
    </>
  );
}